import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { budgetAPI } from '../../services/api'
import useLanguage from '../../hooks/useLanguage'
import toast from 'react-hot-toast'

const GoalsPage = () => {
  const { t } = useTranslation('budget')
  const { locale } = useLanguage()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isContributeModalOpen, setIsContributeModalOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState(null)
  const [contributingGoal, setContributingGoal] = useState(null)
  const [contributeAmount, setContributeAmount] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [formData, setFormData] = useState({
    title: '',
    target_amount: '',
    current_amount: '',
    start_date: new Date().toISOString().split('T')[0],
    deadline: '',
    priority: 'medium',
    color: '#6366f1',
    description: ''
  })

  const queryClient = useQueryClient()

  const { data: goalsResponse, isLoading, error } = useQuery(
    ['goals', statusFilter],
    () => budgetAPI.getGoals(statusFilter !== 'all' ? { status: statusFilter } : {}),
    {
      onError: () => {
        toast.error(t('goals.fetchError'))
      }
    }
  )

  const goals = goalsResponse?.data?.goals || []

  const createMutation = useMutation(
    (goalData) => budgetAPI.createGoal(goalData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['goals'])
        queryClient.invalidateQueries('dashboard-goals')
        queryClient.invalidateQueries('dashboard')
        setIsModalOpen(false)
        resetForm()
        toast.success(t('goals.createSuccess'))
      }
    }
  )

  const updateMutation = useMutation(
    ({ id, ...goalData }) => budgetAPI.updateGoal(id, goalData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['goals'])
        queryClient.invalidateQueries('dashboard-goals')
        queryClient.invalidateQueries('dashboard')
        setIsModalOpen(false)
        setEditingGoal(null)
        resetForm()
        toast.success(t('goals.updateSuccess'))
      }
    }
  )

  const deleteMutation = useMutation(
    (id) => budgetAPI.deleteGoal(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['goals'])
        queryClient.invalidateQueries('dashboard-goals')
        queryClient.invalidateQueries('dashboard')
        toast.success(t('goals.deleteSuccess'))
      }
    }
  )

  const contributeMutation = useMutation(
    ({ id, amount }) => budgetAPI.contributeToGoal(id, { amount }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['goals'])
        queryClient.invalidateQueries('dashboard-goals')
        queryClient.invalidateQueries('dashboard')
        setIsContributeModalOpen(false)
        setContributingGoal(null)
        setContributeAmount('')
        toast.success(t('goals.contributeSuccess'))
      }
    }
  )

  const resetForm = () => {
    setFormData({
      title: '',
      target_amount: '',
      current_amount: '',
      start_date: new Date().toISOString().split('T')[0],
      deadline: '',
      priority: 'medium',
      color: '#6366f1',
      description: ''
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const goalData = {
      title: formData.title,
      target_amount: parseFloat(formData.target_amount),
      start_date: formData.start_date,
      priority: formData.priority,
      ...(formData.current_amount && { current_amount: parseFloat(formData.current_amount) }),
      ...(formData.deadline && { deadline: formData.deadline }),
      ...(formData.color && { color: formData.color }),
      ...(formData.description && { description: formData.description })
    }

    if (editingGoal) {
      updateMutation.mutate({ id: editingGoal.id, ...goalData })
    } else {
      createMutation.mutate(goalData)
    }
  }

  const handleEdit = (goal) => {
    setEditingGoal(goal)
    setFormData({
      title: goal.title || '',
      target_amount: goal.target_amount?.toString() || '',
      current_amount: goal.current_amount?.toString() || '0',
      start_date: goal.start_date ? goal.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
      deadline: goal.deadline ? goal.deadline.split('T')[0] : '',
      priority: goal.priority || 'medium',
      color: goal.color || '#6366f1',
      description: goal.description || ''
    })
    setIsModalOpen(true)
  }

  const handleDelete = (id) => {
    if (window.confirm(t('goals.confirmDelete'))) {
      deleteMutation.mutate(id)
    }
  }

  const handleContribute = (goal) => {
    setContributingGoal(goal)
    setContributeAmount('')
    setIsContributeModalOpen(true)
  }

  const handleContributeSubmit = (e) => {
    e.preventDefault()
    if (!contributingGoal || !contributeAmount) return
    contributeMutation.mutate({
      id: contributingGoal.id,
      amount: parseFloat(contributeAmount)
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'PLN'
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString(locale)
  }

  const getProgress = (goal) => {
    const target = parseFloat(goal.target_amount)
    if (target <= 0) return 0
    return Math.min((parseFloat(goal.current_amount || 0) / target) * 100, 100)
  }

  const getProgressColor = (progress, status) => {
    if (status === 'completed') return 'bg-green-500'
    if (status === 'cancelled') return 'bg-[rgba(29,38,45,0.35)]'
    if (progress >= 75) return 'bg-green-500'
    if (progress >= 50) return 'bg-yellow-500'
    if (progress >= 25) return 'bg-orange-400'
    return 'bg-blue-500'
  }

  const getPriorityLabel = (priority) => {
    return t(`common:priority.${priority}`, { defaultValue: priority })
  }

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-[var(--color-table-header)] text-[var(--color-text-muted)]',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700'
    }
    return colors[priority] || colors.medium
  }

  const getStatusLabel = (status) => {
    return t(`common:goalStatus.${status}`, { defaultValue: status })
  }

  const getDaysRemaining = (deadline) => {
    if (!deadline) return null
    const now = new Date()
    const dl = new Date(deadline)
    const diff = Math.ceil((dl - now) / (1000 * 60 * 60 * 24))
    return diff
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <LoadingSpinner size="lg" text={t('goals.loading')} />
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <div className="text-red-500">
                <p>{t('goals.error')}</p>
                <p className="text-sm mt-2">{error.message}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('goals.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex gap-3 md:ml-4 md:mt-0">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border-[var(--color-border)] shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
          >
            <option value="all">{t('goals.filterAll')}</option>
            <option value="active">{t('goals.filterActive')}</option>
            <option value="completed">{t('goals.filterCompleted')}</option>
            <option value="cancelled">{t('goals.filterCancelled')}</option>
          </select>
          <button
            type="button"
            onClick={() => {
              setEditingGoal(null)
              resetForm()
              setIsModalOpen(true)
            }}
            className="inline-flex items-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93]"
          >
            {t('goals.add')}
          </button>
        </div>
      </div>

      {/* Lista celow */}
      {goals.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {goals.map((goal) => {
            const progress = getProgress(goal)
            const progressColor = getProgressColor(progress, goal.status)
            const daysRemaining = getDaysRemaining(goal.deadline)
            const remaining = Math.max(0, parseFloat(goal.target_amount) - parseFloat(goal.current_amount || 0))

            return (
              <Card key={goal.id}>
                <Card.Body>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {goal.color && (
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: goal.color }}
                        />
                      )}
                      <h3 className="text-lg font-medium text-[var(--color-text)]">{goal.title}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getPriorityColor(goal.priority)}`}>
                        {getPriorityLabel(goal.priority)}
                      </span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-[var(--color-text-muted)]">
                      <span>{formatCurrency(goal.current_amount)}</span>
                      <span>{formatCurrency(goal.target_amount)}</span>
                    </div>
                    <div className="mt-2 w-full bg-[var(--color-skeleton)] rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ${progressColor}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between items-center">
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {progress.toFixed(1)}{t('goals.percentAchieved')}
                      </span>
                      {goal.status === 'active' && remaining > 0 && (
                        <span className="text-xs text-[var(--color-text-muted)]">
                          {t('goals.remaining')} {formatCurrency(remaining)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="mt-4 space-y-1.5">
                    {goal.deadline && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-muted)]">{t('goals.deadline')}</span>
                        <span className={`font-medium ${daysRemaining !== null && daysRemaining < 0 ? 'text-red-600' : daysRemaining !== null && daysRemaining <= 7 ? 'text-yellow-600' : 'text-[var(--color-text)]'}`}>
                          {formatDate(goal.deadline)}
                          {daysRemaining !== null && (
                            <span className="ml-1 text-xs">
                              ({daysRemaining < 0 ? `${Math.abs(daysRemaining)} ${t('goals.daysOverdue')}` : `${daysRemaining} ${t('goals.days')}`})
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--color-text-muted)]">{t('goals.statusLabel')}</span>
                      <span className="text-[var(--color-text)]">{getStatusLabel(goal.status)}</span>
                    </div>
                  </div>

                  {goal.description && (
                    <p className="mt-3 text-sm text-[var(--color-text-muted)] line-clamp-2">{goal.description}</p>
                  )}

                  {/* Akcje */}
                  <div className="mt-4 flex justify-between items-center border-t border-[var(--color-border)] pt-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(goal)}
                        className="text-[#1f9f93] hover:text-[#167e75] text-sm"
                      >
                        {t('common:edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(goal.id)}
                        className="text-red-600 hover:text-red-900 text-sm"
                      >
                        {t('common:delete')}
                      </button>
                    </div>
                    {goal.status === 'active' && (
                      <button
                        onClick={() => handleContribute(goal)}
                        className="inline-flex items-center rounded-md bg-green-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-green-500"
                      >
                        + {t('goals.contribute')}
                      </button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-[var(--color-text-faint)]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-[var(--color-text)]">{t('goals.noGoals')}</h3>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                {t('goals.noGoalsHint')}
              </p>
              <button
                onClick={() => {
                  setEditingGoal(null)
                  resetForm()
                  setIsModalOpen(true)
                }}
                className="mt-4 inline-flex items-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75]"
              >
                {t('goals.addFirst')}
              </button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Modal tworzenia/edycji celu */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-[rgba(29,38,45,0.50)] transition-opacity" onClick={() => { setIsModalOpen(false); setEditingGoal(null); resetForm() }} />

            <div className="relative transform overflow-hidden rounded-lg bg-[var(--color-surface)] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <form onSubmit={handleSubmit}>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-[var(--color-text)]">
                    {editingGoal ? t('goals.editTitle') : t('goals.addTitle')}
                  </h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                        {t('goals.nameLabel')}
                      </label>
                      <input
                        type="text"
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        placeholder={t('goals.namePlaceholder')}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="target_amount" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('goals.targetAmountLabel')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          id="target_amount"
                          value={formData.target_amount}
                          onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder="0.00"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="current_amount" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('goals.currentAmountLabel')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          id="current_amount"
                          value={formData.current_amount}
                          onChange={(e) => setFormData({ ...formData, current_amount: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="start_date" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('goals.startDateLabel')}
                        </label>
                        <input
                          type="date"
                          id="start_date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="deadline" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('goals.deadlineLabel')}
                        </label>
                        <input
                          type="date"
                          id="deadline"
                          value={formData.deadline}
                          onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="priority" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('goals.priorityLabel')}
                        </label>
                        <select
                          id="priority"
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        >
                          <option value="low">{t('common:priority.low')}</option>
                          <option value="medium">{t('common:priority.medium')}</option>
                          <option value="high">{t('common:priority.high')}</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="color" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('goals.colorLabel')}
                        </label>
                        <input
                          type="color"
                          id="color"
                          value={formData.color}
                          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                          className="mt-1 block w-full h-[34px] rounded-md border-0 p-0.5 shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] cursor-pointer"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                        {t('goals.descriptionLabel')}
                      </label>
                      <textarea
                        id="description"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        placeholder={t('goals.descriptionPlaceholder')}
                      />
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93] sm:col-start-2 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? t('common:saving') : (editingGoal ? t('common:save') : t('common:add'))}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false)
                      setEditingGoal(null)
                      resetForm()
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-[var(--color-surface)] px-3 py-2 text-sm font-semibold text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] hover:bg-[var(--color-hover)] sm:col-start-1 sm:mt-0"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal wplaty */}
      {isContributeModalOpen && contributingGoal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-[rgba(29,38,45,0.50)] transition-opacity" onClick={() => { setIsContributeModalOpen(false); setContributingGoal(null) }} />

            <div className="relative transform overflow-hidden rounded-lg bg-[var(--color-surface)] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
              <form onSubmit={handleContributeSubmit}>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-[var(--color-text)]">
                    {t('goals.contributeTitle', { name: contributingGoal.title })}
                  </h3>
                  <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                    {t('goals.collected', { current: formatCurrency(contributingGoal.current_amount), target: formatCurrency(contributingGoal.target_amount) })}
                  </p>
                  <div className="mt-4">
                    <label htmlFor="contribute_amount" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                      {t('goals.contributeAmountLabel')}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      id="contribute_amount"
                      value={contributeAmount}
                      onChange={(e) => setContributeAmount(e.target.value)}
                      className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                      placeholder="0.00"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={contributeMutation.isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 sm:col-start-2 disabled:opacity-50"
                  >
                    {contributeMutation.isLoading ? t('goals.contributing') : t('goals.contribute')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsContributeModalOpen(false)
                      setContributingGoal(null)
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-[var(--color-surface)] px-3 py-2 text-sm font-semibold text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] hover:bg-[var(--color-hover)] sm:col-start-1 sm:mt-0"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GoalsPage
